import type { CountryCode } from 'libphonenumber-js'
import type { TmCountryCode } from '@/services/types'
import type { TmWrappers } from '@/wrappers'
import type { NumberFeatures } from '@/data/models/domain/AvailableToBuyNumbers'
import type { TextWithDropdown } from '@/services/forms/types'
import type { RegistrationNumberType } from '@/services/domain/billing/types'

type IntBool = 0 | 1

export type TollFreeRequestTaxIdentifier = 'EIN' | 'CBN' | 'NEQ' | 'PROVINCIAL_NUMBER'

interface BaseTollFreeRequestServiceApplyBody<T extends string> {
  type: T
}

interface TollFreeRequestServiceApplyPurchaseBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_number_purchase'> {
  phone: string
  country: TmCountryCode
  userId: string
}

interface TollFreeRequestServiceApplyInfoBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_info'> {
  requestId: string
  infoConsent: boolean
}

interface TollFreeRequestServiceApplyBrandUpsertionBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_brand_upsertion'> {
  requestId: string
  entityType: string
  companyNameLegal: string
  companyNameDisplay: string
  taxNumber: string
  taxNumberIdentifier: TollFreeRequestTaxIdentifier
  state: string
  city: string
  street: string
  postalCode: string
  country: string
  verticalTypeOfCompany: string
  website: string
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  contactEmail: string
}

interface TollFreeRequestBrandStepBody<P extends string> extends BaseTollFreeRequestServiceApplyBody<P> {
  requestId: string
}

export interface TollFreeRequestBrandDetailsStepBody extends TollFreeRequestBrandStepBody<'toll_free_brand_org_details'> {
  entityType: string
  companyNameLegal: string
  companyNameDisplay: string
  verticalTypeOfCompany: string
  website: string
  taxNumber?: string
  taxNumberIdentifier?: TollFreeRequestTaxIdentifier
}

interface TollFreeRequestBrandAddressStepBody extends TollFreeRequestBrandStepBody<'toll_free_brand_org_address'> {
  state: string
  city: string
  street: string
  postalCode: string
  country: string
}

interface TollFreeRequestBrandContactStepBody extends TollFreeRequestBrandStepBody<'toll_free_brand_contact'> {
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  contactEmail: string
}

interface TollFreeRequestBrandSubmissionStepBody extends TollFreeRequestBrandStepBody<'toll_free_brand_submission'> {}
interface TollFreeRequestBackToBrandStepBody extends TollFreeRequestBrandStepBody<'toll_free_back_to_brand_action'> {}
interface TollFreeRequestBackToCampaignStepBody extends TollFreeRequestBrandStepBody<'toll_free_back_to_campaign_action'> {}

interface TollFreeRequestServiceApplyCampaignDetailsBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_campaign_details'> {
  contentInfo: string
  estimatedMonthlyMessageVolume: string
  isCanadianTraffic: IntBool
  requestId: string
  subscribersInfo: string
  useCase: string
}

interface TollFreeRequestServiceApplyCampaignOptInFlowBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_campaign_opt_in_flow'> {
  optInDescription: string
  optInScreenshotLink: string
  optInSupportingDocumentation: string
  requestId: string
  termsConditionsUrl: string
  privacyPolicyUrl: string
}

interface TollFreeRequestServiceApplyCampaignSampleMessagesBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_campaign_sample_messages'> {
  requestId: string
  sampleMessages: string[]
}

interface TollFreeRequestServiceApplyCampaignContentAttributesBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_campaign_content_attributes'> {
  requestId: string
  includesLinkInMessageContent: IntBool
  includesPhoneNumberInMessageContent: IntBool
  subscriberHelp: { keyword: string; autoReplyMessage: string }
  subscriberOptIn: { keyword: string; sampleMessage: string }
  subscriberOptOut: { keyword: string; sampleMessage: string }
}

interface TollFreeRequestServiceApplyCampaignSubmissionBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_campaign_submission'> {
  requestId: string
}

interface TollFreeRequestServiceApplyEditRequestBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_edit_request'> {
  requestId: string
}

interface TollFreeRequestServiceApplySubmissionForReviewBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_submission_for_review'> {
  requestId: string
}

interface TollFreeRequestServiceApplyBackStepActionBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_back_step_action'> {
  requestId: string
}

interface TollFreeRequestServiceApplyRegisterBody extends BaseTollFreeRequestServiceApplyBody<'toll_free_number_selection'> {
  phone: string
}

export type TollFreeRequestServiceApplyBody =
  | TollFreeRequestServiceApplyPurchaseBody
  | TollFreeRequestServiceApplyInfoBody
  | TollFreeRequestServiceApplyBrandUpsertionBody
  | TollFreeRequestServiceApplyCampaignDetailsBody
  | TollFreeRequestServiceApplyCampaignOptInFlowBody
  | TollFreeRequestServiceApplyCampaignSampleMessagesBody
  | TollFreeRequestServiceApplyCampaignContentAttributesBody
  | TollFreeRequestServiceApplyCampaignSubmissionBody
  | TollFreeRequestServiceApplyEditRequestBody
  | TollFreeRequestServiceApplySubmissionForReviewBody
  | TollFreeRequestServiceApplyBackStepActionBody
  | TollFreeRequestServiceApplyRegisterBody
  | TollFreeRequestBrandDetailsStepBody
  | TollFreeRequestBrandAddressStepBody
  | TollFreeRequestBrandContactStepBody
  | TollFreeRequestBrandSubmissionStepBody
  | TollFreeRequestBackToBrandStepBody
  | TollFreeRequestBackToCampaignStepBody

export type TollFreeRequestServiceApplyResponseStep =
  | 'introduced'
  | 'tollfree_number_selection'
  | 'tollfree_number_activation'
  | 'tollfree_info'
  | 'tollfree_brand_creation'
  | 'tollfree_brand_edit'
  | 'tollfree_campaign_creation'
  | 'tollfree_campaign_edit'
  | 'tollfree_submission_for_review'
  | 'tollfree_submitted_for_review'
  | 'tollfree_outgoing_registration'
  | 'tollfree_verified'

export interface TollFreeRequestServiceApplyResponse {
  requestId: string
  nextStep: TollFreeRequestServiceApplyResponseStep
}

export type TollFreeCampaignDetailsFormSchemeCanadianTraffic = 'yes' | 'no'

export interface TollFreeOptInFlowFormScheme {
  optInDescription: string
  optInScreenshotLink: string | null
  optInScreenshot: File | null
  optInSupportingDocumentation: string
  termsConditionsUrl: string
  privacyPolicyUrl: string
}

export interface TollFreeCampaignDetailsFormScheme {
  useCase: string
  subscribersInfo: string
  contentInfo: string
  estimatedMonthlyMessageVolume: string
  canadianTraffic: TollFreeCampaignDetailsFormSchemeCanadianTraffic | ''
}

export type TollFreeAutoFillWithAiFormCountry = Extract<TmCountryCode, 'US' | 'CA'>
export interface TollFreeAutoFillWithAiFormScheme {
  organizationName: string
  useCase: string
  website: string
  taxNumber: TextWithDropdown<TollFreeRequestTaxIdentifier>
  country: TollFreeAutoFillWithAiFormCountry
}

export interface TollFreeSummary {
  selectedNumber: string | null
  brandName: string
  contactEmail: string
  contactPhone: string
  useCase: string
  campaignDescription: string
  optInFlowDescription: string
  sampleMessages: string
  features: NumberFeatures[]
}

export enum TollFreeRequestStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  VERIFIED = 'VERIFIED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
}

export interface TollFreeFormModalParams {
  requestId: string
}

export const tollFreeFormModalId: TmWrappers = 'tollFreeFormModal'

export enum TollFreeBrandEntityType {
  /**
   * @deprecated please use {@link TollFreeBrandEntityType.RegisteredCorporation}
   */
  Franchise = 'Franchise',
  GovernmentOrStateOrganisation = 'Government or State Organisation',
  /**
   * @deprecated please use {@link TollFreeBrandEntityType.SoleProprietor}
   */
  LicensedProfessional = 'Licensed Professional',
  PublicUtility = 'Public Utility',
  RegisteredCorporation = 'Registered Corporation',
  SoleProprietor = 'Sole Proprietor',
  NonProfitOrganization = 'Non-Profit Organization',
}

export interface StartPreFillRequestParams {
  organizationName: string
  useCase: string
  website: string
  taxNumber: string
  taxNumberIdentifier: RegistrationNumberType
  country: CountryCode
}

export enum TollFreeCampaignStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  VERIFIED = 'VERIFIED',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
}

export type TollFreeRequestCampaign = {
  id: string
  useCase: string | null
  status: TollFreeCampaignStatus
  registeredAt: string | null
  updatedAt: string | null
  subscribersInfo: string | null
  contentInfo: string | null
  isCanadianTraffic: boolean | null
  estimatedMonthlyMessageVolume: string | null
  optInDescription: string | null
  optInScreenshotLink: string | null
  optInSupportingDocumentation: string | null
  termsConditionsUrl: string | null
  privacyPolicyUrl: string | null
  sampleMessages: string[] | null
  errorFields: string[] | null
  isCampaignDetailsFilled: boolean
  isOptInFlowFilled: boolean
  isSampleMessagesFilled: boolean
}
